import React from "react"
import { Box, Typography, Grid, Card, CardContent, Alert, useTheme, alpha } from "@mui/material"
import { CheckCircle, Info } from "@mui/icons-material"
import OutlinedInput from "src/shared/components/outlined-input"
import type { StepComponentProps } from "../types"
import { AUTOMATIC_TYPES, VALIDATION_RULES } from "../constants"
import SelectProduct from "src/shared/container/select-product"

const AuditConfiguration: React.FC<StepComponentProps> = ({ formData, onFieldChange }) => {
  const theme = useTheme()
  if (formData.type === "all") {
    return (
      <Box textAlign="center" py={4}>
        <CheckCircle sx={{ fontSize: 64, color: "success.main", mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          All Products Selected
        </Typography>
        <Typography variant="body2" color="text.secondary">
          All products in the selected location will be included in the audit.
        </Typography>
      </Box>
    )
  }

  if (formData.type === "automatic") {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Automatic Selection Criteria
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Choose how products should be automatically selected based on sales data
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {AUTOMATIC_TYPES.map((type) => (
            <Grid item xs={12} md={4} key={type.value}>
              <Card
                sx={{
                  cursor: "pointer",
                  border: `2px solid ${
                    formData.automaticType === type.value ? theme.palette.primary.main : "transparent"
                  }`,
                  backgroundColor:
                    formData.automaticType === type.value ? alpha(theme.palette.primary.main, 0.05) : "inherit",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.02),
                    border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`
                  },
                  height: "100%",
                  transition: "all 0.2s ease-in-out"
                }}
                onClick={() => onFieldChange("automaticType", type.value)}
              >
                <CardContent sx={{ textAlign: "center", py: 3 }}>
                  {type.icon}
                  <Typography variant="subtitle2" fontWeight="medium" sx={{ mt: 1, mb: 1 }}>
                    {type.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {type.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <Info sx={{ fontSize: 16, mr: 1, verticalAlign: "text-bottom" }} />
            Product selection will be based on sales data from the last 90 days
          </Typography>
        </Alert>

        <OutlinedInput
          label="Percentage of Products"
          type="number"
          value={formData.percentage || ""}
          onChange={(event) => onFieldChange("percentage", parseInt(event.target.value) || 0)}
          placeholder="Enter percentage (max 10%)"
          helperText={`Enter the percentage of products to audit (maximum ${VALIDATION_RULES.MAX_PERCENTAGE}%)`}
          error={formData.percentage ? formData.percentage > VALIDATION_RULES.MAX_PERCENTAGE : false}
          required
          inputProps={{
            min: VALIDATION_RULES.MIN_PERCENTAGE,
            max: VALIDATION_RULES.MAX_PERCENTAGE
          }}
        />
      </Box>
    )
  }

  if (formData.type === "specific") {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Select Specific Products
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Choose the specific products you want to include in the audit
        </Typography>

        <SelectProduct
          multiple
          selectedProducts={(formData.specificProducts as any[]) ?? []}
          onChange={(value: any) => onFieldChange("specificProducts", value || [])}
        />
      </Box>
    )
  }

  return null
}

export default AuditConfiguration
